import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import CheckSvg from 'images/v5/icons/check.svg';
import Button from 'layout/atoms/Button/Button';
import Dropdown from 'layout/atoms/Dropdown/Dropdown';
import Label from 'layout/atoms/Label';
import Loader from 'layout/atoms/Loader/Loader';
import Modal from 'layout/atoms/Modal/Modal';
import ParameterDetails from 'layout/atoms/ParameterDetails/ParameterDetails';
import TableGrid, { Body, Cell, Head, Row } from 'layout/atoms/TableGrid/TableGrid';
import Grid from 'layout/molecules/Grid/Grid';
import { selectModalByName } from 'store/layoutSlice';
import { selectOrderDetailsItem } from 'store/orderDetailsSlice';
import { fetchSpecificationDetails, resetSpecificationProductionManagerState, selectOrderProductionManagerData, selectOrderProductionManagerSpecificationDetails, selectOrderProductionManagerStatus, setSpecificationAllocationsReady, submitSpecificationAllocations, updateVariantAssignedItems, } from 'store/orderProductionManagerSlice';
function SpecificationProductionManager() {
    var _a, _b;
    const { t, i18n } = useTranslation(['specification', 'orders']);
    const dispatch = useDispatch();
    const modalData = (_a = useSelector(selectModalByName('specificationProductionManager'))) === null || _a === void 0 ? void 0 : _a.data;
    const orderItem = useSelector(selectOrderDetailsItem);
    const orderProductionManagerData = useSelector(selectOrderProductionManagerData);
    const specificationData = useSelector(selectOrderProductionManagerSpecificationDetails);
    const status = useSelector(selectOrderProductionManagerStatus);
    const variantItemsChanges = useSelector((state) => state.orderProductionManager.variantItemsChanges);
    const productionManagerItem = (_b = orderProductionManagerData === null || orderProductionManagerData === void 0 ? void 0 : orderProductionManagerData.specifications) === null || _b === void 0 ? void 0 : _b.find((item) => { var _a; return ((_a = item === null || item === void 0 ? void 0 : item.specification) === null || _a === void 0 ? void 0 : _a.id) === (modalData === null || modalData === void 0 ? void 0 : modalData.specificationId); });
    const isPreviewMode = (modalData === null || modalData === void 0 ? void 0 : modalData.productionApproved) === true || (productionManagerItem === null || productionManagerItem === void 0 ? void 0 : productionManagerItem.productionApproved) === true;
    const previewGridTemplateColumns = [
        ['150px', '18%'],
        ['120px', '14%'],
        ['210px', '26%'],
        ['100px', '14%'],
        ['100px', '14%'],
        ['120px', '14%'],
    ];
    const editableGridTemplateColumns = [
        ['150px', '15%'],
        ['120px', '12%'],
        ['210px', '21%'],
        ['100px', '10%'],
        ['100px', '10%'],
        ['80px', '8%'],
        ['80px', '8%'],
        ['80px', '8%'],
        ['80px', '8%'],
    ];
    const gridTemplateColumns = isPreviewMode
        ? previewGridTemplateColumns
        : editableGridTemplateColumns;
    useEffect(() => {
        if ((modalData === null || modalData === void 0 ? void 0 : modalData.specificationId) && (orderItem === null || orderItem === void 0 ? void 0 : orderItem.id)) {
            dispatch(fetchSpecificationDetails({
                orderId: orderItem.id,
                specificationId: modalData.specificationId,
            }));
        }
        return () => {
            dispatch(resetSpecificationProductionManagerState());
        };
    }, [dispatch, modalData === null || modalData === void 0 ? void 0 : modalData.specificationId, orderItem === null || orderItem === void 0 ? void 0 : orderItem.id]);
    if (status.fetchSpecificationDetails === 'loading' || !specificationData) {
        return (React.createElement(Modal, { name: "specificationProductionManager", size: "fullWidth" },
            React.createElement(Loader, { fullViewport: true })));
    }
    if (status.fetchSpecificationDetails === 'failed') {
        return (React.createElement(Modal, { name: "specificationProductionManager", size: "fullWidth" },
            React.createElement("p", null, "B\u0142\u0105d podczas \u0142adowania danych specyfikacji")));
    }
    const allVariants = specificationData.components
        ? specificationData.components.flatMap((comp) => comp.componentVariants.map((variant) => {
            var _a;
            return ({
                componentName: comp.component.name || '',
                componentCategory: ((_a = comp.component.productCategory) === null || _a === void 0 ? void 0 : _a.typeLabelPL) || '',
                variant,
            });
        }))
        : [];
    const handleCancel = () => {
        dispatch({ type: 'layout/closeModal', payload: { name: 'specificationProductionManager' } });
    };
    const handleSubmit = () => {
        var _a;
        if ((_a = specificationData === null || specificationData === void 0 ? void 0 : specificationData.specification) === null || _a === void 0 ? void 0 : _a.id) {
            const allAllocated = allVariants.every((item) => {
                var _a, _b, _c;
                const required = Number((_a = item.variant.requiredConsumption) !== null && _a !== void 0 ? _a : 0);
                const assignedState = (_b = variantItemsChanges[item.variant.id]) === null || _b === void 0 ? void 0 : _b.assignedItems;
                const assigned = typeof assignedState === 'number'
                    ? assignedState
                    : Number((_c = item.variant.allocatedQuantity) !== null && _c !== void 0 ? _c : 0);
                return assigned + 0.000001 >= required;
            });
            dispatch(setSpecificationAllocationsReady({
                specificationId: specificationData.specification.id,
                ready: allAllocated,
            }));
        }
        if ((modalData === null || modalData === void 0 ? void 0 : modalData.specificationId) && (orderItem === null || orderItem === void 0 ? void 0 : orderItem.id)) {
            dispatch(submitSpecificationAllocations({
                orderId: orderItem.id,
                specificationId: modalData.specificationId,
            }));
        }
        dispatch({ type: 'layout/closeModal', payload: { name: 'specificationProductionManager' } });
    };
    return (React.createElement(Modal, { alwaysScroll: true, name: "specificationProductionManager", size: "fullWidth", header: React.createElement(Label, { weight: "strong" },
            "Manager produkcji / ",
            specificationData.specification.itemCode || '-'), footer: React.createElement(React.Fragment, null,
            React.createElement(Button, { theme: "border", onClick: handleCancel }, isPreviewMode ? t('common:close') : t('cancel')),
            !isPreviewMode && (React.createElement(Button, { color: "primary", leftIcon: React.createElement(CheckSvg, null), onClick: handleSubmit, submit: true }, t('common:done')))) },
        React.createElement(Grid, { gap: 24 },
            React.createElement(ParameterDetails, { size: "xl", label: specificationData.specification.itemCode || '-', value: specificationData.specification.fancyName || '-' }),
            isPreviewMode && (React.createElement("div", { style: { color: '#697483', fontSize: '14px' } }, t('orders:production_manager_preview_mode_message'))),
            allVariants.length === 0 ? (React.createElement("div", { style: { textAlign: 'center', padding: '20px' } }, "Brak wariant\u00F3w komponent\u00F3w")) : (React.createElement(TableGrid, { columns: gridTemplateColumns, borders: true },
                React.createElement(Head, null,
                    React.createElement(Row, { head: true },
                        React.createElement(Cell, null, "Komponent"),
                        React.createElement(Cell, null, "Kolor"),
                        React.createElement(Cell, null, "Szczeg\u00F3\u0142y"),
                        React.createElement(Cell, null, "Dostawca"),
                        React.createElement(Cell, null, "Operator"),
                        React.createElement(Cell, null, "Przydzielone"),
                        !isPreviewMode && React.createElement(Cell, null),
                        !isPreviewMode && React.createElement(Cell, null, "Dost\u0119pne"),
                        !isPreviewMode && React.createElement(Cell, null))),
                React.createElement(Body, null, allVariants.map((item, index) => {
                    var _a, _b, _c, _d, _e, _f, _g, _h;
                    const variantAttributes = {
                        en: item.variant.variantAttributesEN,
                        pl: item.variant.variantAttributesPL,
                    }[i18n.language];
                    const componentAttributes = {
                        en: item.variant.componentAttributesEN,
                        pl: item.variant.componentAttributesPL,
                    }[i18n.language];
                    const displayDetails = variantAttributes || componentAttributes || item.variant.name || '-';
                    const consumptionTypeLabel = ((_a = item.variant.componentCategory) === null || _a === void 0 ? void 0 : _a.consumptionType)
                        ? {
                            en: item.variant.componentCategory.consumptionType.labelEN,
                            pl: item.variant.componentCategory.consumptionType.labelPL,
                        }[i18n.language]
                        : '';
                    // Spec-scoped required consumption (computed on backend, filtered to this specification).
                    const requiredConsumption = (_b = item.variant.requiredConsumption) !== null && _b !== void 0 ? _b : 0;
                    const formattedConsumption = requiredConsumption
                        ? `${parseFloat(requiredConsumption.toFixed(2))} ${consumptionTypeLabel}`.trim()
                        : '-';
                    const variantItemsState = variantItemsChanges[item.variant.id];
                    const currentAssigned = (_c = variantItemsState === null || variantItemsState === void 0 ? void 0 : variantItemsState.assignedItems) !== null && _c !== void 0 ? _c : 0;
                    const currentAvailable = (_d = variantItemsState === null || variantItemsState === void 0 ? void 0 : variantItemsState.availableItems) !== null && _d !== void 0 ? _d : 0;
                    const remainingNeed = Math.max(0, requiredConsumption - currentAssigned);
                    const maxDelta = Math.min(currentAvailable, remainingNeed);
                    return (React.createElement(Row, { key: item.variant.id || index },
                        React.createElement(Cell, null, item.componentName),
                        React.createElement(Cell, null, item.variant.color || '-'),
                        React.createElement(Cell, null, displayDetails),
                        React.createElement(Cell, null, ((_f = (_e = item.variant.supplier) === null || _e === void 0 ? void 0 : _e.supplier) === null || _f === void 0 ? void 0 : _f.shortName) || '-'),
                        React.createElement(Cell, null, ((_h = (_g = item.variant.orderOperatorOfOrder) === null || _g === void 0 ? void 0 : _g.orderOperator) === null || _h === void 0 ? void 0 : _h.name) || '-'),
                        React.createElement(Cell, null,
                            currentAssigned,
                            ' ',
                            React.createElement("span", { style: { color: 'grey' } },
                                "/ ",
                                formattedConsumption)),
                        !isPreviewMode && (React.createElement(Cell, null,
                            React.createElement("div", { style: {
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'flex-start',
                                    gap: '2px',
                                } },
                                React.createElement(Button, { shape: "squared", onClick: () => {
                                        if (maxDelta <= 0) {
                                            return;
                                        }
                                        dispatch(updateVariantAssignedItems({
                                            variantId: item.variant.id,
                                            delta: 1,
                                        }));
                                    } }, "+"),
                                React.createElement(Button, { shape: "squared", onClick: () => dispatch(updateVariantAssignedItems({
                                        variantId: item.variant.id,
                                        delta: -1,
                                    })) }, "\u2212")))),
                        !isPreviewMode && React.createElement(Cell, null, currentAvailable),
                        !isPreviewMode && (React.createElement(Cell, null,
                            React.createElement(Dropdown, null,
                                React.createElement(Button, { align: "left", theme: "ghost", onClick: () => {
                                        if (maxDelta > 0) {
                                            dispatch(updateVariantAssignedItems({
                                                variantId: item.variant.id,
                                                delta: maxDelta,
                                            }));
                                        }
                                    } },
                                    "Przydziel wszystkie (",
                                    maxDelta,
                                    ")"))))));
                })))))));
}
export default SpecificationProductionManager;
